import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useCart } from '@/contexts/CartContext.jsx'
import { useAuth } from '@/AuthContext'
import { useDeliveryMode } from '@/contexts/DeliveryModeContext'
import { db } from '@/lib/firebase'
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore'

function Tab({ to, label, icon, hidden, badge, onClick, isActive, showBadge = true, hideText = false }) {
  const [badgeAnim, setBadgeAnim] = React.useState(false)
  const [pulseAnim, setPulseAnim] = React.useState(false)
  const prevBadge = React.useRef(badge)
  
  React.useEffect(() => {
    if (Number(badge) > Number(prevBadge.current)) {
      setBadgeAnim(true)
      setTimeout(() => setBadgeAnim(false), 300)
    }
    prevBadge.current = badge
  }, [badge])

  React.useEffect(() => {
    if (isActive) {
      setPulseAnim(true)
      setTimeout(() => setPulseAnim(false), 600)
    }
  }, [isActive])

  if (hidden) return null

  const handleClick = (e) => {
    if (window.navigator.vibrate) window.navigator.vibrate(15)
    if (onClick) onClick(e)
  }

  const baseClasses = [
    'relative shrink-0 flex flex-col items-center justify-center gap-0.5 py-1.5 px-1.5 min-w-[60px] snap-center',
    'text-[10px] font-medium transition-all duration-200 ease-out',
    'hover:scale-105 active:scale-95',
    'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:ring-offset-2'
  ]

  const activeClasses = isActive 
    ? 'text-white bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl mx-0.5 px-2 shadow-lg shadow-brand-500/25' 
    : 'text-ink/60 hover:text-ink hover:bg-ink/5 rounded-lg mx-0.5 px-1.5'

  if (onClick) {
    return (
      <button
        type="button"
        className={[...baseClasses, activeClasses].join(' ')}
        aria-label={label}
        onClick={handleClick}
      >
        <span className={`relative transition-transform duration-200 ${pulseAnim ? 'scale-110' : 'scale-100'}`} aria-hidden>
          {icon}
          {showBadge && Number(badge) > 0 && (
            <span
              className={[
                "absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1.5 rounded-full",
                "bg-gradient-to-r from-rose-500 to-rose-600 text-white text-[10px] font-bold",
                "leading-[20px] text-center shadow-lg border-2 border-white",
                "transition-all duration-300 ease-out",
                badgeAnim ? "scale-125 rotate-12" : "scale-100 rotate-0"
              ].join(' ')}
              aria-hidden
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        {!hideText && <span className="leading-none tracking-wide">{label}</span>}
      </button>
    )
  }

  return (
    <NavLink
      to={to}
      className={({ isActive: navActive }) => [
        ...baseClasses,
        navActive ? 'text-white bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl mx-0.5 px-2 shadow-lg shadow-brand-500/25' : 'text-ink/60 hover:text-ink hover:bg-ink/5 rounded-lg mx-0.5 px-1.5'
      ].join(' ')}
      aria-label={label}
      onClick={handleClick}
    >
      <span className={`relative transition-transform duration-200 ${pulseAnim ? 'scale-110' : 'scale-100'}`} aria-hidden>
        {icon}
        {showBadge && Number(badge) > 0 && (
          <span
            className={[
              "absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1.5 rounded-full",
              "bg-gradient-to-r from-rose-500 to-rose-600 text-white text-[10px] font-bold",
              "leading-[20px] text-center shadow-lg border-2 border-white",
              "transition-all duration-300 ease-out",
              badgeAnim ? "scale-125 rotate-12" : "scale-100 rotate-0"
            ].join(' ')}
            aria-hidden
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {!hideText && <span className="leading-none tracking-wide">{label}</span>}
    </NavLink>
  )
}

export default function MobileTabBar({
  showRider,
  showAdmin,
  showAccounting,
  showDeliveryManagement,
  showAffiliate,
  onOpenMenu,
  onOpenNotification,
  // New props for smart menu
  isStoreManager,
  isPacker,
  isCashier,
  isTreasurer,
  isManager,
  isDelivery,
}) {
  const { pathname } = useLocation()
  const { isDeliveryMode } = useDeliveryMode()
  const hidden = false
  if (hidden) return null

  // Cart items count (total quantity, not distinct items)
  const { items, totalQty } = useCart()
  const cartCount = Array.isArray(items) ? totalQty : 0

  // Rider active jobs count (assigned or out_for_delivery)
  const { user } = useAuth()
  const uid = user?.uid || ''
  const [jobsCount, setJobsCount] = React.useState(0)
  React.useEffect(() => {
    if (!showRider || !uid) { setJobsCount(0); return }
    const q = query(
      collection(db, 'deliveries'),
      where('riderId', '==', uid),
      limit(100)
    )
    const unsub = onSnapshot(q, (snap) => {
      let c = 0
      snap.forEach((doc) => {
        const s = String(doc.data()?.status || '')
        if (s === 'assigned' || s === 'out_for_delivery') c++
      })
      setJobsCount(c)
    }, () => setJobsCount(0))
    return () => unsub()
  }, [showRider, uid])

  // Enhanced notification count with unread tracking
  const [notifCount, setNotifCount] = React.useState(0)
  const [unreadCount, setUnreadCount] = React.useState(0)
  React.useEffect(() => {
    if (!uid) { setNotifCount(0); setUnreadCount(0); return }
    
    // Listen to user notifications
    const q1 = query(collection(db, 'notifications'), where('uid', '==', uid), limit(100))
    const q2 = query(collection(db, 'notifications'), where('riderUid', '==', uid), limit(100))
    
    const unsubs = [
      onSnapshot(q1, snap => {
        let total = 0, unread = 0
        snap.forEach(doc => {
          const data = doc.data()
          total++
          if (!data?.read) unread++
        })
        setNotifCount(prev => Math.max(prev, total))
        setUnreadCount(prev => Math.max(prev, unread))
      }),
      onSnapshot(q2, snap => {
        let total = 0, unread = 0
        snap.forEach(doc => {
          const data = doc.data()
          total++
          if (!data?.read) unread++
        })
        setNotifCount(prev => Math.max(prev, total))
        setUnreadCount(prev => Math.max(prev, unread))
      })
    ]
    return () => unsubs.forEach(u => u())
  }, [uid])

  // Orders count (ongoing orders only, excluding completed)
  const [ordersCount, setOrdersCount] = React.useState(0)
  React.useEffect(() => {
    if (!uid) { setOrdersCount(0); return }
    
    const q = query(
      collection(db, 'orders'),
      where('buyerId', '==', uid),
      limit(100)
    )
    
    const unsub = onSnapshot(q, (snap) => {
      let count = 0
      snap.forEach((doc) => {
        const status = String(doc.data()?.status || '')
        // Count only ongoing orders (exclude completed)
        if (status && status !== 'completed' && status !== 'cancelled') {
          count++
        }
      })
      setOrdersCount(count)
    }, () => setOrdersCount(0))
    
    return () => unsub()
  }, [uid])

  // Smart tab visibility based on current page
  const isHomePage = pathname === '/'
  const isStorePage = pathname.startsWith('/store')
  const isDashboardPage = pathname.startsWith('/dashboard')
  const isReportsPage = pathname.startsWith('/reports')
  const isAdminPage = pathname.startsWith('/admin')
  const isAccountingPage = pathname.startsWith('/accounting')
  const isRiderPage = pathname.startsWith('/rider')
  const isAffiliatePage = pathname.startsWith('/affiliate')

  // Enhanced icons with better visual appeal
  const icon = {
    home: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1v-9.5z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    jobs: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M3 7h6l2 3h10v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    store: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M4 7h16l-2 10a2 2 0 01-2 2H8a2 2 0 01-2-2L4 7z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 7a4 4 0 10-8 0" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    reports: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M4 19V5a1 1 0 011-1h10l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 17v-4M13 17v-6M17 17v-2" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    dashboard: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M3 3h8v8H3V3zM13 3h8v5h-8V3zM13 10h8v11h-8V10zM3 13h8v8H3v-8z" 
              stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
      </svg>
    ),
    admin: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/>
        <path d="M19.4 15a7.96 7.96 0 00.4-3 7.96 7.96 0 00-.4-3l2.1-1.6-2-3.5-2.5 1A8.1 8.1 0 0014 1.6l-.5-2.6h-3L10 1.6A8.1 8.1 0 006.9 3l-2.5-1-2 3.5L4.5 7.1A7.96 7.96 0 004 10a7.96 7.96 0 00.4 3L2.3 14.6l2 3.5 2.5-1A8.1 8.1 0 0010 22.4l.5 2.6h3l.5-2.6a8.1 8.1 0 003.1-1.9l2.5 1 2-3.5-2.2-1.6z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    accounting: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M5 3h14a1 1 0 011 1v16l-4-3-4 3-4-3-4 3V4a1 1 0 011-1z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    notification: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M18 16v-5a6 6 0 10-12 0v5a2 2 0 002 2h8a2 2 0 002-2z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M13.73 21a2 2 0 01-3.46 0" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    // New icons for smart menu
    storeManagement: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M3 21h18l-1-7H4l-1 7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M9 3v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M15 3v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    orderProcessing: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15 2H9a1 1 0 00-1 1v2a1 1 0 001 1h6a1 1 0 001-1V3a1 1 0 00-1-1z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12h6M9 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    deliveries: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M1 3h15l4 7-4 7H1V3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 21a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2"/>
        <path d="M20 21a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    treasurerPayments: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M12 1v6l3-3 3 3V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" 
              stroke="currentColor" strokeWidth="2"/>
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    // New icons for guest menu
    membership: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    login: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    affiliate: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
    orders: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 1v6l3-3 3 3V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    wallet: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M19 7H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 21V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v16" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 9h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    refresh: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 3v5h-5" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 21v-5h5" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    services: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="drop-shadow-sm">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  }

  // Smart bottom menu with role-based selection
  const getSmartBottomMenu = () => {
    const tabs = []

    // Check if user is authenticated
    const isAuthenticated = user && user.uid

    // DELIVERY MODE: Show only delivery-specific tabs
    if (isDeliveryMode && isAuthenticated) {
      tabs.push({
        to: "/rider/job-claim",
        label: "Claim Job",
        icon: icon.jobs,
        badge: jobsCount,
        isActive: isRiderPage,
        priority: 1
      })

      tabs.push({
        to: "/rider/my-jobs",
        label: "My Jobs",
        icon: icon.deliveries,
        isActive: pathname.startsWith('/rider/my-jobs'),
        priority: 2
      })

      tabs.push({
        to: "/rider/wallet",
        label: "Wallet",
        icon: icon.wallet,
        isActive: pathname.startsWith('/rider/wallet'),
        priority: 3
      })

      tabs.push({
        to: "/rider/analytics",
        label: "Analytics",
        icon: icon.reports,
        isActive: pathname.startsWith('/rider/analytics'),
        priority: 4
      })

      // Add refresh button as the 5th tab
      tabs.push({
        onClick: () => window.location.reload(),
        label: "Refresh",
        icon: icon.refresh,
        isActive: false,
        priority: 5
      })

      return tabs
    }

    // For guests/non-authenticated users
    if (!isAuthenticated) {
      tabs.push({
        to: "/",
        label: "Home",
        icon: icon.home,
        isActive: isHomePage,
        priority: 1
      })

      tabs.push({
        to: "/store",
        label: "Stores",
        icon: icon.store,
        badge: cartCount,
        isActive: isStorePage,
        priority: 2
      })

      tabs.push({
        to: "/service-marketplace",
        label: "Services",
        icon: icon.services,
        isActive: pathname.startsWith('/service-marketplace'),
        priority: 3
      })

      tabs.push({
        to: "/become-member",
        label: "Become a Member",
        icon: icon.membership,
        isActive: pathname.startsWith('/become-member'),
        priority: 4
      })

      tabs.push({
        to: "/signup",
        label: "Login/Signup",
        icon: icon.login,
        isActive: pathname.startsWith('/signup'),
        priority: 5
      })

      // Add a placeholder for the 5th slot to maintain consistent spacing
      tabs.push({
        to: "/",
        label: "",
        icon: <div></div>, // Empty icon
        hidden: true, // Hide this tab
        priority: 5
      })

      return tabs.filter(tab => !tab.hidden)
    }

    // For authenticated users - ALWAYS VISIBLE - Core items (2 slots)
    tabs.push({
      to: "/",
      label: "Home",
      icon: icon.home,
      isActive: isHomePage,
      priority: 1
    })

    tabs.push({
      to: "/store",
      label: "Stores",
      icon: icon.store,
      badge: cartCount,
      isActive: isStorePage,
      priority: 2
    })

    // Services - available to all authenticated users
    tabs.push({
      to: "/service-marketplace",
      label: "Services",
      icon: icon.services,
      isActive: pathname.startsWith('/service-marketplace'),
      priority: 3
    })

    // ROLE-BASED SMART SELECTION (3 additional slots)
    // Priority order: Role-specific primary function first (3rd position), then secondary, then reports/dashboard last

    // For Delivery Users (Riders) - Primary: Rider Jobs (4th position)
    if (showRider) {
      tabs.push({
        to: "/rider/job-claim",
        label: "Rider Jobs",
        icon: icon.jobs,
        badge: jobsCount,
        isActive: isRiderPage,
        priority: 4
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 5
      })
      // Remove reports to keep 5 tabs total
    }
    // For Packers - Primary: Order Processing (4th position)
    else if (isPacker) {
      tabs.push({
        to: "/order-processing",
        label: "Order Processing",
        icon: icon.orderProcessing,
        isActive: pathname.startsWith('/order-processing'),
        priority: 4
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 5
      })
      // Remove reports to keep 5 tabs total
    }
    // For Cashiers - Primary: Deliveries (3rd position)
    else if (isCashier) {
      tabs.push({
        to: "/delivery-management",
        label: "Deliveries",
        icon: icon.deliveries,
        isActive: pathname === '/delivery-management',
        priority: 3
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      // Third slot: Reports (last)
      tabs.push({
        to: "/reports",
        label: "Reports",
        icon: icon.reports,
        isActive: isReportsPage,
        priority: 5
      })
    }
    // For Store Managers - Primary: Store Management (3rd position)
    else if (isStoreManager) {
      tabs.push({
        to: "/store-manager",
        label: "Store Management",
        icon: icon.storeManagement,
        isActive: pathname.startsWith('/store-manager'),
        priority: 3
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      // Third slot: Reports (last)
      tabs.push({
        to: "/reports",
        label: "Reports",
        icon: icon.reports,
        isActive: isReportsPage,
        priority: 5
      })
    }
    // For Admins - Primary: Admin (3rd position)
    else if (showAdmin) {
      tabs.push({
        to: "/admin/coop-membership",
        label: "Admin",
        icon: icon.admin,
        isActive: isAdminPage,
        priority: 3
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      // Third slot: Reports (last)
      tabs.push({
        to: "/reports",
        label: "Reports",
        icon: icon.reports,
        isActive: isReportsPage,
        priority: 5
      })
    }
    // For Treasurers - Primary: Accounting (3rd position)
    else if (isTreasurer) {
      tabs.push({
        to: "/accounting",
        label: "Accounting",
        icon: icon.accounting,
        isActive: isAccountingPage,
        priority: 3
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      // Third slot: Reports (last)
      tabs.push({
        to: "/reports",
        label: "Reports",
        icon: icon.reports,
        isActive: isReportsPage,
        priority: 5
      })
    }
    // For Affiliate Users - Primary: Affiliate Dashboard (3rd position)
    else if (showAffiliate) {
      tabs.push({
        to: "/affiliate",
        label: "Affiliate",
        icon: icon.affiliate,
        isActive: isAffiliatePage,
        priority: 3
      })
      // Second slot: Dashboard
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      // Third slot: Reports (last)
      tabs.push({
        to: "/reports",
        label: "Reports",
        icon: icon.reports,
        isActive: isReportsPage,
        priority: 5
      })
    }
    // For General Members - Fallback
    else {
      // Show Dashboard first
      tabs.push({
        to: "/dashboard",
        label: "Dashboard",
        icon: icon.dashboard,
        isActive: isDashboardPage,
        priority: 4
      })
      if (showAccounting) {
        tabs.push({
          to: "/accounting",
          label: "Accounting",
          icon: icon.accounting,
          isActive: isAccountingPage,
          priority: 5
        })
      } else {
        // If no accounting access, show reports
        tabs.push({
          to: "/reports",
          label: "Reports",
          icon: icon.reports,
          isActive: isReportsPage,
          priority: 5
        })
      }
    }

    // Return exactly 5 tabs (2 core + 3 role-specific)
    return tabs.slice(0, 5)
  }

  const smartBottomMenu = getSmartBottomMenu()

  return (
    <>
      {/* Enhanced Tab Bar */}
      <div className={`md:hidden fixed inset-x-0 bottom-0 z-20 border-t border-border/60 bg-white/95 backdrop-blur-xl supports-[padding:env(safe-area-inset-bottom)]:pb-[env(safe-area-inset-bottom)] transition-transform duration-300 translate-y-0`} aria-label="Primary mobile navigation">
         {/* Subtle top shadow for depth */}
         <div className="absolute inset-x-0 -top-1 h-1 bg-gradient-to-b from-black/5 to-transparent" />
         
         <nav className="mx-auto page-boxed page-gutter h-12 pb-[max(env(safe-area-inset-bottom),0.25rem)]">
          <div className="flex items-stretch gap-1 overflow-x-auto overflow-y-hidden no-scrollbar snap-x snap-mandatory relative px-2">
           {/* Smart bottom menu tabs */}
           {smartBottomMenu.map((tab, index) => (
             <Tab 
               key={index}
               to={tab.to}
               label={tab.label}
               icon={tab.icon}
               hidden={tab.hidden}
               badge={tab.badge}
               onClick={tab.onClick}
               isActive={tab.isActive}
               showBadge={tab.showBadge}
               hideText={false}
             />
           ))}
          </div>
        </nav>
      </div>
    </>
  )
}